import React, { useState } from 'react'
import {
	Button,
	Checkbox,
	Input,
	Popconfirm,
	Popover
} from 'antd'
import {
	ArrowLeftOutlined,
	CloseOutlined,
	EditOutlined,
	SearchOutlined
} from '@ant-design/icons'
import { LABEL_LINKED_ENTITY_TYPE, ManagerTaskLabel, ManagerTaskLabelAssignmentData } from './types'
import {
	createManagerTaskLabel,
	deleteManagerTaskLabel,
	updateManagerTaskLabel
} from './managerTaskApi'
import EntitySearchDrawer from './EntitySearchDrawer'

const PALETTE = [
	'#b7ddb0', '#f8df72', '#f4a261', '#eb8a8a', '#d6b4fc',
	'#5cb85c', '#d4ac0d', '#e67e22', '#c0392b', '#9b59b6',
	'#27ae60', '#7d6608', '#ba4a00', '#922b21', '#4a235a',
	'#d6eaf8', '#aed6f1', '#d5f5e3', '#fde8d8', '#e5e8e9',
	'#3498db', '#45b7d1', '#82e0aa', '#f0b27a', '#95a5a6',
	'#1a5276', '#1b4f72', '#145a32', '#7e5109', '#515a5a'
]

const ENTITY_TYPE_LABELS: Record<LABEL_LINKED_ENTITY_TYPE, string> = {
	[LABEL_LINKED_ENTITY_TYPE.JOB]: 'Zákazky',
	[LABEL_LINKED_ENTITY_TYPE.FINISH]: 'Ukončenie',
	[LABEL_LINKED_ENTITY_TYPE.RATING]: 'Hodnotenie',
	[LABEL_LINKED_ENTITY_TYPE.PAYSLIP]: 'Výplatné pásky',
	[LABEL_LINKED_ENTITY_TYPE.USER]: 'Používatelia',
	[LABEL_LINKED_ENTITY_TYPE.PRICE_OFFER]: 'Cenová ponuka',
	[LABEL_LINKED_ENTITY_TYPE.COMPLAINT]: 'Reklamácie'
}

interface Props {
	value: number[]
	onChange: (ids: number[]) => void
	labels: ManagerTaskLabel[]
	canEdit: boolean
	onLabelsChanged: () => void
	taskId?: number
	labelAssignments?: Record<number, ManagerTaskLabelAssignmentData | null | undefined>
	onPendingEntityAssignment?: (labelId: number, fkField: string, entityId: number, dataName: string) => void
	autoOpen?: boolean
	onDismiss?: () => void
}

const LabelPickerPopover = ({
	value = [],
	onChange,
	labels,
	canEdit,
	onLabelsChanged,
	taskId,
	labelAssignments,
	onPendingEntityAssignment,
	autoOpen,
	onDismiss
}: Props) => {
	const [open, setOpen] = useState(() => autoOpen ?? false)
	const [view, setView] = useState<'list' | 'edit'>('list')
	const [searchText, setSearchText] = useState('')
	const [editing, setEditing] = useState<ManagerTaskLabel | null>(null)
	const [draftName, setDraftName] = useState('')
	const [draftColor, setDraftColor] = useState<string | null>(null)
	const [draftLinkedEntityType, setDraftLinkedEntityType] = useState<LABEL_LINKED_ENTITY_TYPE | null>(null)
	const [showEntityTypePicker, setShowEntityTypePicker] = useState(false)
	const [entitySearchLabel, setEntitySearchLabel] = useState<ManagerTaskLabel | null>(null)
	const [localEntityNames, setLocalEntityNames] = useState<Record<number, string>>({})

	const filteredLabels = labels.filter((label) => label.name.toLowerCase().includes(searchText.toLowerCase()))

	const save = async () => {
		if (!draftName.trim()) return
		if (editing) {
			await updateManagerTaskLabel(editing.id, { name: draftName.trim(), color: draftColor || '' })
		} else {
			await createManagerTaskLabel({
				name: draftName.trim(),
				color: draftColor || '',
				linkedEntityType: draftLinkedEntityType
			})
		}
		onLabelsChanged()
		setView('list')
	}

	const handleDelete = async () => {
		if (!editing) return
		await deleteManagerTaskLabel(editing.id)
		onChange(value.filter((v) => v !== editing.id))
		onLabelsChanged()
		setView('list')
	}

	const handleCheckboxChange = (label: ManagerTaskLabel, checked: boolean) => {
		if (checked) {
			onChange([...value, label.id])
			if (label.linkedEntityType) {
				setEntitySearchLabel(label)
			}
		} else {
			onChange(value.filter((v) => v !== label.id))
		}
	}

	const selectedLabels = labels.filter((label) => value.includes(label.id))

	const trigger = selectedLabels.length > 0 ? (
		<div style={{
			display: 'flex',
			flexWrap: 'wrap',
			gap: 4,
			alignItems: 'center',
			cursor: 'pointer'
		}}>
			{selectedLabels.map((label) => {
				const assignment = labelAssignments?.[label.id]
				const entityId = assignment?.jobId
					?? assignment?.finishId
					?? assignment?.ratingId
					?? assignment?.payslipId
					?? assignment?.userId
					?? assignment?.priceOfferId
					?? assignment?.complaintId
				const entityDisplayName = localEntityNames[label.id] || assignment?.dataName
				return (
					<span
						key={label.id}
						style={{
							background: label.color || '#2563eb',
							color: '#fff',
							padding: '2px 10px',
							borderRadius: 12,
							fontSize: 12,
							fontWeight: 500
						}}
					>
						<span style={{ fontWeight: 600 }}>{label.name}</span>
						{(entityDisplayName || entityId) && (
							<>
								<span style={{
									margin: '0 4px',
									opacity: 0.7
								}}>
									·
								</span>
								<span style={{ fontWeight: 700 }}>
									{entityDisplayName || `#${entityId}`}
								</span>
							</>
						)}
					</span>
				)
			})}
			<span style={{
				display: 'inline-flex',
				alignItems: 'center',
				padding: '2px 10px',
				borderRadius: 12,
				fontSize: 12,
				fontWeight: 500,
				border: '1px dashed #9ca3af',
				color: '#6b7280',
				cursor: 'pointer'
			}}>
				+ Pridať
			</span>
		</div>
	) : (
		<span style={{
			color: '#9ca3af',
			fontSize: 13,
			cursor: 'pointer'
		}}>
			(+ pridať labels)
		</span>
	)

	const listView = (
		<div>
			<Input
				placeholder={'Search labels...'}
				prefix={<SearchOutlined />}
				value={searchText}
				onChange={(e) => setSearchText(e.target.value)}
				style={{ marginBottom: 8 }}
			/>
			<div style={{
				maxHeight: 300,
				overflowY: 'auto'
			}}>
				{filteredLabels.map((label) => {
					const assignment = labelAssignments?.[label.id]
					const entityId = label.linkedEntityType
						? (assignment?.jobId ?? assignment?.finishId ?? assignment?.ratingId ?? assignment?.payslipId ?? assignment?.userId ?? assignment?.priceOfferId ?? assignment?.complaintId)
						: null
					const entityDisplayName = label.linkedEntityType ? (localEntityNames[label.id] || assignment?.dataName) : null
					return (
						<div
							key={label.id}
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: 8,
								marginBottom: 4
							}}
						>
							<Checkbox
								checked={value.includes(label.id)}
								onChange={(e) => handleCheckboxChange(label, e.target.checked)}
							/>
							<div style={{ flex: 1 }}>
								<div style={{
									background: label.color || '#eee',
									height: 32,
									borderRadius: 4,
									display: 'flex',
									alignItems: 'center',
									padding: '0 8px',
									color: '#fff',
									gap: 4,
									overflow: 'hidden'
								}}>
									<span style={{
										whiteSpace: 'nowrap',
										flexShrink: 0
									}}>
										{label.name}
									</span>
									{label.linkedEntityType && (
										<>
											<span style={{
												opacity: 0.7,
												flexShrink: 0
											}}>
												·
											</span>
											<span style={{
												fontWeight: 700,
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis'
											}}>
												{entityDisplayName || (entityId ? `#${entityId}` : `Dáta: ${ENTITY_TYPE_LABELS[label.linkedEntityType as LABEL_LINKED_ENTITY_TYPE]}`)}
											</span>
										</>
									)}
								</div>
							</div>
							{canEdit && (
								<EditOutlined
									style={{ cursor: 'pointer', color: '#6b7280' }}
									onClick={() => {
										setEditing(label)
										setDraftName(label.name)
										setDraftColor(label.color || null)
										setDraftLinkedEntityType(null)
										setShowEntityTypePicker(false)
										setView('edit')
									}}
								/>
							)}
						</div>
					)
				})}
			</div>
			{canEdit && (
				<Button
					block
					style={{ marginTop: 8 }}
					onClick={() => {
						setEditing(null)
						setDraftName('')
						setDraftColor(null)
						setDraftLinkedEntityType(null)
						setShowEntityTypePicker(false)
						setView('edit')
					}}
				>
					Create a new label
				</Button>
			)}
		</div>
	)

	const renderLinkedEntityTypeSection = () => {
		if (editing !== null) {
			if (editing.linkedEntityType) {
				return (
					<span style={{
						display: 'inline-flex',
						alignItems: 'center',
						padding: '2px 10px',
						background: '#f3f4f6',
						color: '#6b7280',
						borderRadius: 12,
						fontSize: 12,
						fontWeight: 500,
						cursor: 'default'
					}}>
						{ENTITY_TYPE_LABELS[editing.linkedEntityType as LABEL_LINKED_ENTITY_TYPE]}
					</span>
				)
			}
			return (
				<span
					onClick={() => setShowEntityTypePicker(true)}
					style={{
						display: 'inline-flex',
						alignItems: 'center',
						padding: '2px 10px',
						background: '#f3f4f6',
						color: '#6b7280',
						borderRadius: 12,
						fontSize: 12,
						fontWeight: 500,
						cursor: 'pointer'
					}}
				>
					+ Pridať
				</span>
			)
		}

		if (draftLinkedEntityType) {
			return (
				<span
					onClick={() => { setDraftLinkedEntityType(null); setShowEntityTypePicker(true) }}
					style={{
						display: 'inline-flex',
						alignItems: 'center',
						padding: '2px 10px',
						background: '#f3f4f6',
						color: '#6b7280',
						borderRadius: 12,
						fontSize: 12,
						fontWeight: 500,
						cursor: 'pointer'
					}}
				>
					{ENTITY_TYPE_LABELS[draftLinkedEntityType]}
				</span>
			)
		}

		if (showEntityTypePicker) {
			return (
				<div style={{
					display: 'flex',
					flexWrap: 'wrap',
					gap: 6
				}}>
					{Object.values(LABEL_LINKED_ENTITY_TYPE).map((et) => (
						<span
							key={et}
							onClick={() => { setDraftLinkedEntityType(et); setShowEntityTypePicker(false) }}
							style={{
								display: 'inline-flex',
								alignItems: 'center',
								padding: '2px 10px',
								background: '#e5e7eb',
								color: '#374151',
								borderRadius: 12,
								fontSize: 12,
								fontWeight: 500,
								cursor: 'pointer'
							}}
						>
							{ENTITY_TYPE_LABELS[et]}
						</span>
					))}
				</div>
			)
		}

		return (
			<span
				onClick={() => setShowEntityTypePicker(true)}
				style={{
					display: 'inline-flex',
					alignItems: 'center',
					padding: '2px 10px',
					background: '#f3f4f6',
					color: '#6b7280',
					borderRadius: 12,
					fontSize: 12,
					fontWeight: 500,
					cursor: 'pointer'
				}}
			>
				+ Pridať
			</span>
		)
	}

	const editView = (
		<div>
			<div style={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				marginBottom: 12
			}}>
				<ArrowLeftOutlined
					style={{ cursor: 'pointer', color: '#374151' }}
					onClick={() => setView('list')}
				/>
				<span style={{
					fontWeight: 600,
					fontSize: 14,
					color: '#111827'
				}}>
					{editing ? 'Edit label' : 'Create label'}
				</span>
				<CloseOutlined
					style={{ cursor: 'pointer', color: '#9ca3af' }}
					onClick={() => setOpen(false)}
				/>
			</div>

			<div style={{
				background: draftColor || '#eee',
				height: 40,
				borderRadius: 4,
				padding: '0 12px',
				display: 'flex',
				alignItems: 'center',
				color: '#fff',
				fontWeight: 500,
				marginBottom: 12
			}}>
				{draftName || '(no name)'}
			</div>

			<div style={{ marginBottom: 8, fontSize: 13, color: '#374151' }}>Title</div>
			<Input
				value={draftName}
				onChange={(e) => setDraftName(e.target.value)}
				style={{ marginBottom: 12 }}
			/>

			<div style={{ marginBottom: 8, fontSize: 13, color: '#374151' }}>Select a color</div>
			<div style={{
				display: 'grid',
				gridTemplateColumns: 'repeat(6, 1fr)',
				gap: 6,
				marginBottom: 8
			}}>
				{PALETTE.map((c) => (
					<div
						key={c}
						onClick={() => setDraftColor(c)}
						style={{
							background: c,
							height: 28,
							borderRadius: 4,
							cursor: 'pointer',
							border: draftColor === c ? '2px solid #000' : '2px solid transparent'
						}}
					/>
				))}
			</div>

			<Button
				size={'small'}
				style={{ marginBottom: 12 }}
				onClick={() => setDraftColor(null)}
			>
				x Remove color
			</Button>

			<div style={{
				marginTop: 16, marginBottom: 8, fontSize: 13, color: '#374151'
			}}>
				Vyber dáta
			</div>
			{renderLinkedEntityTypeSection()}

			<div style={{
				display: 'flex',
				gap: 8,
				justifyContent: 'flex-end',
				marginTop: 16
			}}>
				<Button type={'primary'} onClick={save}>Save</Button>
				{editing && (
					<Popconfirm title={'Naozaj zmazať?'} onConfirm={handleDelete}>
						<Button danger>Delete</Button>
					</Popconfirm>
				)}
			</div>
		</div>
	)

	return (
		<>
			<Popover
				trigger={'click'}
				visible={open}
				onVisibleChange={(v) => {
					// Don't close the popover while the entity drawer is open —
					// clicking inside the drawer triggers an outside-click on the popover.
					if (!v && entitySearchLabel !== null) return
					setOpen(v)
					if (!v) {
						setView('list')
						setSearchText('')
						if (value.length === 0) {
							onDismiss?.()
						}
					}
				}}
				overlayInnerStyle={{ width: 360 }}
				content={view === 'list' ? listView : editView}
			>
				{trigger}
			</Popover>
			{entitySearchLabel && (
				<EntitySearchDrawer
					entityType={entitySearchLabel.linkedEntityType!}
					taskId={taskId}
					labelId={entitySearchLabel.id}
					onAssigned={(displayName) => {
						setLocalEntityNames((prev) => ({ ...prev, [entitySearchLabel.id]: displayName }))
						setEntitySearchLabel(null)
						onLabelsChanged()
					}}
					onSelect={(fkField, entityId, displayName) => {
						const labelId = entitySearchLabel.id
						setLocalEntityNames((prev) => ({ ...prev, [labelId]: displayName }))
						setEntitySearchLabel(null)
						onPendingEntityAssignment?.(labelId, fkField, entityId, displayName)
					}}
					onClose={() => setEntitySearchLabel(null)}
				/>
			)}
		</>
	)
}

export default LabelPickerPopover
